import React, { useEffect, useState } from "react";
import * as Mui from "@mui/material";
import { CheckCircleRounded, ErrorOutlineRounded } from "@mui/icons-material";
import { GlobalSidePanel } from "components/DesignSystem/GlobalSidePanel";
import { RequestInfoBanner } from "components/DesignSystem/RequestInfoBanner";
import { PanelActionFooter, PrimaryButton } from "components/CustomComponents/DesignSystemButtons";
import { designColors, designType } from "services/constants/designTokens";
import { Field, FormSection, panelBodySx } from "pages/PayRoll/PayInputs/Shared/formUi";
import { SelectField } from "pages/PayRoll/PayInputs/Shared/fieldInputs";
import { PreviewBox, Required } from "pages/PayRoll/Salary/Shared/salaryUi";
import { PlainInput } from "pages/PayRoll/Shared/statutoryUi";
import { APPLIES_TO_OPTIONS, MONTHS, emptySlab, slabCheck, slabFieldErrors, sortSlabs } from "./ptConfigData";
import { checkPtSlabs, testMonthOf, testPtEmployee } from "./ptConfigApi";

// Edit Slab drawer (PT1b): wage from, wage to (blank = and above), PT
// amount, who it applies to and — when the site has a special month — the
// amount in that month. The gap / overlap check covers every slab (POST
// config/slabs/check/) and Test on employee runs the payroll calculation
// with the unsaved form (POST config/test-employee/). Nothing here saves:
// Save puts the slab back into the form.

const SERVER_DELAY = 600;

export const PtSlabPanel = ({ open, slab, form, siteName, readOnly, domain, companyId, siteId, employees, appliesToOptions, onClose, onSave }) => {
  const adding = !slab;
  const [draft, setDraft] = useState(() => slab || emptySlab());
  const [showErrors, setShowErrors] = useState(false);
  const [employeeId, setEmployeeId] = useState("");
  const [serverCheck, setServerCheck] = useState(null);
  const [test, setTest] = useState({ loading: false, error: "", data: null });

  useEffect(() => {
    if (!open) return;
    if (slab) {
      setDraft({ ...slab });
    } else {
      // A new slab starts the rupee after the current top slab of the All set.
      const top = sortSlabs(form.slabs.filter((item) => item.applies_to === "ALL")).slice(-1)[0];
      setDraft(emptySlab({ wage_from: top && top.wage_to !== "" ? String(Number(top.wage_to) + 1) : "" }));
    }
    setShowErrors(false);
    setServerCheck(null);
    setTest({ loading: false, error: "", data: null });
    // Only when the drawer opens or another slab is picked.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, slab]);

  // First employee once the list arrives.
  useEffect(() => {
    if (open && !employeeId && employees?.length) setEmployeeId(employees[0].id);
  }, [open, employeeId, employees]);

  const specialOn = form.special_month_on;
  const set = (key) => (value) => setDraft((current) => ({ ...current, [key]: value }));
  const merged = [...form.slabs.filter((item) => item.id !== draft.id), draft];
  const errors = slabFieldErrors(draft, specialOn);
  const error = (key) => (showErrors ? errors[key] : undefined);
  const complete = !Object.keys(errors).length;
  const localCheck = slabCheck(merged, specialOn);

  // The server's check over every slab once this slab's fields are filled.
  const slabsKey = JSON.stringify({ slabs: merged, special: specialOn ? form.special_month : "" });
  useEffect(() => {
    if (!open || !complete) {
      setServerCheck(null);
      return undefined;
    }
    // The local check shows until the server answers for these slabs.
    setServerCheck(null);
    const request = new AbortController();
    const timer = setTimeout(() => {
      const { slabs, special } = JSON.parse(slabsKey);
      checkPtSlabs({ domain, companyId, slabs, specialMonth: special, signal: request.signal })
        .then((data) => setServerCheck({ valid: Boolean(data?.ok), message: String(data?.message || errorsText(data?.errors)).trim().replace(/^✓\s*/, "") }))
        .catch((err) => {
          if (err.name !== "AbortError") setServerCheck(null);
        });
    }, SERVER_DELAY);
    return () => {
      clearTimeout(timer);
      request.abort();
    };
  }, [open, complete, slabsKey, domain, companyId]);
  const check = serverCheck?.message ? serverCheck : localCheck;

  // Test on employee with the unsaved form (this slab included).
  const month = testMonthOf(form);
  const formKey = complete && form.frequency ? JSON.stringify({ ...form, slabs: merged }) : "";
  useEffect(() => {
    if (!open || !formKey || !employeeId) {
      setTest({ loading: false, error: "", data: null });
      return undefined;
    }
    const request = new AbortController();
    setTest((current) => ({ ...current, loading: true, error: "" }));
    const timer = setTimeout(() => {
      testPtEmployee({ domain, companyId, siteId, employeeId, month, form: JSON.parse(formKey), signal: request.signal })
        .then((data) => setTest({ loading: false, error: "", data }))
        .catch((err) => {
          if (err.name !== "AbortError") setTest({ loading: false, error: err.message, data: null });
        });
    }, SERVER_DELAY);
    return () => {
      clearTimeout(timer);
      request.abort();
    };
  }, [open, formKey, employeeId, month, domain, companyId, siteId]);

  const save = () => {
    setShowErrors(true);
    if (!complete) return;
    onSave(draft);
  };

  const amountInput = (key, label, helper, required = true, placeholder, wide = false) => (
    <Field wide={wide} label={<>{label}{required ? <Required /> : null}</>} helper={helper} error={error(key)}>
      <PlainInput type="number" value={draft[key]} onChange={set(key)} disabled={readOnly} error={error(key)} placeholder={placeholder} inputProps={{ min: 0, step: 1 }} />
    </Field>
  );

  return (
    <GlobalSidePanel
      open={open}
      onClose={onClose}
      title={adding ? "Add Slab" : readOnly ? "Slab" : "Edit Slab"}
      subtitle={`${siteName} · ${form.name}`}
      bodySx={panelBodySx}
      footer={
        readOnly ? (
          <Mui.Stack direction="row" justifyContent="flex-end" sx={{ width: "100%" }}>
            <PrimaryButton onClick={onClose}>Close</PrimaryButton>
          </Mui.Stack>
        ) : (
          <PanelActionFooter onCancel={onClose} primaryLabel={adding ? "Add Slab" : "Save"} primaryProps={{ onClick: save }} />
        )
      }
    >
      <Mui.Stack spacing={2}>
        {readOnly ? <RequestInfoBanner tone="warning" title="This version is locked." body="A payroll has used it. Create a new version to change slabs." /> : null}

        <RequestInfoBanner title="PT Slab" body="A wage range and the PT deducted for it. Slabs must cover every wage from ₹0." />

        <FormSection title="Slab" info="Whole rupees. Both ends are included.">
          {amountInput("wage_from", "Wage from (₹)", "Lowest wage in this range.")}
          {amountInput("wage_to", "Wage to (₹)", "Highest wage. Blank = and above.", false, "and above")}
          {/* Two per row: Wage from | Wage to, PT amount | Applies to, then the special-month amount on its own row. */}
          {amountInput("amount", "PT amount (₹)", "PT deducted for this range.")}
          <Field label={<>Applies to<Required /></>} helper="All, Women or Men.">
            <SelectField
              value={draft.applies_to}
              onChange={(value) => value && set("applies_to")(value)}
              options={appliesToOptions?.length ? appliesToOptions : APPLIES_TO_OPTIONS}
              disabled={readOnly}
            />
          </Field>
          {specialOn
            ? amountInput(
                "special_month_amount",
                `Amount in ${MONTHS[Number(form.special_month) - 1] || "special month"} (₹)`,
                "PT deducted in that month instead.",
                true,
                undefined,
                true,
              )
            : null}
        </FormSection>

        <FormSection title="Check" info="Gaps and overlaps across all slabs.">
          <Mui.Stack direction="row" spacing={0.75} alignItems="center" sx={{ gridColumn: "1 / -1" }}>
            {check.valid ? <CheckCircleRounded sx={{ fontSize: 16, color: "#12B76A" }} /> : <ErrorOutlineRounded sx={{ fontSize: 16, color: "#DC2626" }} />}
            <Mui.Typography sx={{ ...designType.captionText, color: check.valid ? "#027A48" : "#DC2626" }}>{check.message}</Mui.Typography>
          </Mui.Stack>
        </FormSection>

        <FormSection title="Test on employee" info="Runs the same calculation as payroll with this unsaved form. Nothing is saved.">
          <Field wide label="Employee">
            <SelectField
              value={employeeId}
              onChange={(value) => setEmployeeId(value || employees?.[0]?.id || "")}
              options={(employees || []).map((item) => ({ value: item.id, label: [item.name, item.code, item.site].filter(Boolean).join(" · ") }))}
              placeholder={employees ? "Pick an employee" : "Loading employees…"}
              disabled={!employees}
            />
          </Field>
          <Mui.Box sx={{ gridColumn: "1 / -1" }}>
            <PreviewBox>
              <TestResult form={form} complete={complete} test={test} noEmployees={Boolean(employees) && !employees.length} />
            </PreviewBox>
          </Mui.Box>
        </FormSection>
      </Mui.Stack>
    </GlobalSidePanel>
  );
};

function errorsText(errors) {
  if (!errors || typeof errors !== "object") return "";
  return Object.values(errors)
    .flat()
    .filter((item) => typeof item === "string")
    .join(" ");
}

const line = (text, color = designColors.slate600) => <Mui.Typography sx={{ ...designType.helperText, color }}>{text}</Mui.Typography>;

// sentence ("Priya N · Oct 2026 – Mar 2027: … (₹71 this month)"), the
// estimate note, the working and any warnings.
const TestResult = ({ form, complete, test, noEmployees }) => {
  if (!form.frequency) return line("Pick how often PT is deducted first.");
  if (!complete) return line("Fill the fields above to test this slab.");
  if (noEmployees) return line("No employees to test on.");
  if (test.loading) return line("Working it out…");
  if (test.error) return line(test.error, "#DC2626");
  const data = test.data;
  if (!data) return line("Pick an employee to test this slab.");
  return (
    <Mui.Stack spacing={0.5}>
      {data.note ? <Mui.Typography sx={designType.captionText}>{data.note}</Mui.Typography> : null}
      <Mui.Typography sx={{ ...designType.tableBodyCell, color: designColors.slate900 }}>{data.sentence || "—"}</Mui.Typography>
      {data.steps.map((step) => (
        <Mui.Typography key={step} sx={designType.captionText}>
          {step}
        </Mui.Typography>
      ))}
      {data.warnings.map((warning) => (
        <Mui.Typography key={warning} sx={{ ...designType.captionText, color: "#B54708" }}>
          {warning}
        </Mui.Typography>
      ))}
    </Mui.Stack>
  );
};
